import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import React, { useMemo } from 'react';
import SmallQuestionNode from 'pages/Flow/Nodes/QuestionNode/SmallQuesionNode';
import Typography from '@mui/material/Typography';
import { ASYNC_STATUS } from 'utils/constants/flow.constant';
import { DEFAULT_PAUSE_LEVEL_VALUE, DEFAULT_START_LEVEL_VALUE } from 'redux/reducer/FlowReducer/constants';
import { DISPLAY_Q_INPUT_TYPES } from 'utils/constants/question.constant';
import {
  FlowNode, FlowNodeBody, FlowNodeHeader, NodeSkeleton,
} from 'pages/Flow/Nodes/FlowNode';
import { HHandle } from 'pages/Flow/Nodes/HHandle';
import { NODES_OBJECT, NODES_TYPE } from 'pages/Flow/constants';
import { Position } from 'reactflow';
import { habuColors } from 'Theme';
import { openSidePanel } from 'redux/actions/FlowAction';
import { styled } from '@mui/material/styles';
import { useActionMenuItems } from 'pages/Flow/Nodes/QuestionNode/useActionMenuItems';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';


const StyledLevelIndicator = styled(Box, {
  shouldForwardProp: (prop) => ![
    'isFlowCachedMode',
    'selectedStartLevel',
    'selectedPauseLevel',
  ].includes(prop),
})(({
  theme,
  isSelected,
  isSelectedForRun,
}) => {
  const backgroundColor = isSelected && theme.palette.primary.main
    || isSelectedForRun && theme.palette.primary.light;

  return {
    position: 'absolute',
    display: 'inline-flex',
    width: '32px',
    height: '32px',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '-5px',
    marginTop: '-9px',
    background: backgroundColor || theme.palette.background.light,
    borderRight: `1px solid ${backgroundColor || theme.palette.secondary.light}`,
    borderBottom: `1px solid ${backgroundColor || theme.palette.secondary.light}`,
    fontWeight: theme.typography.fontWeightBold,
  };
});

const StyledIconIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'grid',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '24px',
  width: '32px',
  height: '32px',
  marginLeft: '-5px',
  borderRight: `1px solid ${theme.palette.secondary.light}`,
  borderBottom: `1px solid ${theme.palette.secondary.light}`,
}));



const QuestionHeading = ({ text = '' }) => (
  <Typography variant='subtitle3' sx={{ color: habuColors.neutral['60'] }}>
    {text}
  </Typography>
);

const QuestionDetailsNode = ({
  qId,
  selected,
  nodeId,
  qNode,
  isFlowRunDialog = false,
}) => {
  const { crId } = useParams();
  const dispatch = useDispatch();
  const nodeSize = useSelector(state => state.flow.nodeSize);
  const isFlowCachedMode = useSelector(state => state.flow.isFlowCachedMode);
  const {
    status: nodesCompleteDetailsStatus,
  } = useSelector(state => state.flow.nodesCompleteDetails);

  const nodeDetails = useMemo(() => qNode?.data ?? {}, [qNode?.data]);
  const question = useMemo(() => qNode?.data.question ?? {}, [qNode?.data.question]);
  const nodeTags = useMemo(() => nodeDetails?.tags ?? '', [nodeDetails]);

  const isListQuestion = question?.isUserListQuestion;
  const questionType = isListQuestion ? 'List Question' : 'Analytical Question';
  const level = qNode?.data?.level ?? 0;

  // Create an enhanced qNode with the latest question data for the side panel
  const enhancedQNode = useMemo(() => {
    if (!question) return qNode;

    return {
      ...qNode,
      data: {
        ...qNode?.data,
        ...question,
        level: qNode?.data?.level ?? 0,
        nodeId,
        referenceID: qNode?.data?.referenceID || qId,
      },
    };
  }, [qNode, question, nodeId, qId]);

  const questionNodeAction = useActionMenuItems();
  const actionMenuItems = useMemo(() => (
    questionNodeAction(enhancedQNode, enhancedQNode?.data, crId)
  ), [questionNodeAction, enhancedQNode, crId]);

  const selectedStartLevel = useSelector((state) => state.flow.selectedStartLevel);
  const selectedPauseLevel = useSelector((state) => state.flow.selectedPauseLevel);

  const isQNodeSelected = isFlowCachedMode && selectedStartLevel === question?.level;

  const onInfoClick = () => {
    dispatch(openSidePanel({ type: 'NODE', data: enhancedQNode }));
  };

  // If not loaded, show loading UI
  if (nodesCompleteDetailsStatus === ASYNC_STATUS.LOADING) {
    return (
      <NodeSkeleton type={NODES_TYPE.question} selected={selected} />
    );
  }

  const willRunAsPartOfCachedRun = !isQNodeSelected
    && selectedPauseLevel === DEFAULT_PAUSE_LEVEL_VALUE
    && ((selectedStartLevel === DEFAULT_START_LEVEL_VALUE && level > 0)
        || level >= selectedStartLevel);

  // Create a consistent style for all section items
  const itemStyle = {
    fontSize: '0.875rem',
    lineHeight: '1.43',
    padding: '3px 0',
  };

  const tagStyle = {
    background: habuColors.neutral[5],
    color: habuColors.neutral[90],
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '0.75rem',
    display: 'inline-block',
    margin: '2px 0',
  };

  const MediumQuestionNode = (
    <FlowNode
      type={NODES_TYPE.question}
      selected={selected || isQNodeSelected}
      nodeLevel={level}
      isFlowCachedMode={isFlowCachedMode || isFlowRunDialog}
      selectedStartLevel={selectedStartLevel}
      selectedPauseLevel={selectedPauseLevel}
      id={nodeId}
    >
      <HHandle type='target' position={Position.Left} />
      <FlowNodeHeader
        Icon={NODES_OBJECT[NODES_TYPE.question].Icon}
        menuItems={actionMenuItems}
        showMenuItems={!(isFlowCachedMode || isFlowRunDialog)}
      >
        <Grid container alignItems='baseline'>
          <Grid item xs zeroMinWidth>
            <Typography noWrap pl={1}>
              {question?.title}
            </Typography>
          </Grid>
          {!(isFlowCachedMode || isFlowRunDialog)
            && (
              <Grid item xs='auto'>
                <IconButton edge='end' aria-label='comments' onClick={onInfoClick}>
                  <InfoOutlinedIcon fontSize='small' />
                </IconButton>
              </Grid>
            )}
        </Grid>
      </FlowNodeHeader>
      <FlowNodeBody>
        {isFlowCachedMode && (
          <StyledLevelIndicator
            isSelected={isQNodeSelected}
            isSelectedForRun={willRunAsPartOfCachedRun}
          >
            {level}
          </StyledLevelIndicator>
        )}
        {isFlowCachedMode
          && String(selectedStartLevel) === String(level)
          && String(selectedStartLevel) !== String(selectedPauseLevel) && (
          <StyledIconIndicator>
            <PlayArrowIcon style={{
              width: '16px',
              height: '16px',
            }}
            />
          </StyledIconIndicator>
        )}
        {(isFlowCachedMode || isFlowRunDialog) && String(selectedPauseLevel) === String(level) && (
          <StyledIconIndicator>
            <PauseIcon style={{
              width: '16px',
              height: '16px',
            }}
            />
          </StyledIconIndicator>
        )}
        <Grid container spacing={2} sx={{ pl: 4, marginLeft: '-12px' }}>
          <Grid item xs={12}>
            <QuestionHeading text='QUESTION TYPE' />
            <Typography sx={itemStyle}>{questionType}</Typography>
          </Grid>
          {!(isFlowCachedMode || isFlowRunDialog) && (
            <Grid item xs={12} container>
              <Grid item xs={12}>
                <QuestionHeading text='DATASET' />
              </Grid>
              {question?.dataTypes && question?.dataTypes.map((dt) => (
                <Grid item xs={12} key={dt?.ID || `dt-${Math.random()}`}>
                  <Typography sx={itemStyle}>
                    {`@${dt?.macro}(${dt?.importDataType?.displayName || dt?.importDataType?.name})`}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}
          {!(isFlowCachedMode || isFlowRunDialog) && (
            <Grid item xs={12} container>
              <Grid item xs={12}>
                <QuestionHeading text='RUN TIME PARAMETERS' />
              </Grid>
              {question?.parameters && question?.parameters.map((param) => (
                <Grid item xs={12} key={param?.displayName || `param-${Math.random()}`}>
                  <Typography sx={itemStyle}>
                    {`${param?.displayName} (${DISPLAY_Q_INPUT_TYPES[param?.type]?.label})`}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}
          {!(isFlowCachedMode || isFlowRunDialog) && (
            <Grid item xs={12} container>
              <QuestionHeading text='OUTPUT' />
              {isListQuestion ? (
                <>
                  {question?.userListOutputs && question?.userListOutputs.map((metric) => (
                    <Grid item xs={12} key={metric?.ID || `userlist-${Math.random()}`}>
                      <Typography sx={itemStyle}>
                        {`${metric?.name} (${DISPLAY_Q_INPUT_TYPES[metric?.type]?.label})`}
                      </Typography>
                    </Grid>
                  ))}
                </>
              ) : (
                <>
                  {question?.dimensions && question?.dimensions.map((dimension) => (
                    <Grid item xs={12} key={dimension?.ID || `dimension-${Math.random()}`}>
                      <Typography sx={itemStyle}>
                        {`${dimension?.displayName} (${DISPLAY_Q_INPUT_TYPES[dimension?.type]?.label})`}
                      </Typography>
                    </Grid>
                  ))}
                  {question?.metrics && question?.metrics.map((metric) => (
                    <Grid item xs={12} key={metric?.ID || `metric-${Math.random()}`}>
                      <Typography sx={itemStyle}>
                        {`${metric?.displayName} (${DISPLAY_Q_INPUT_TYPES[metric?.type]?.label})`}
                      </Typography>
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
          )}
          {nodeTags && (
            <Grid item xs={12} container>
              <Grid item xs={12}>
                <QuestionHeading text='TAGS' />
              </Grid>
              <Grid item xs={12}>
                <Box display='flex' flexWrap='wrap' gap={0.5} mt={0.5}>
                  {nodeTags.split(',').filter(tag => tag.trim()).map((tag) => (
                    <Typography
                      key={`tag-${tag.trim()}-${nodeId}`}
                      variant='body2'
                      sx={tagStyle}
                    >
                      {tag.trim()}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            </Grid>
          )}
        </Grid>
      </FlowNodeBody>
      <HHandle type='source' position={Position.Right} />
    </FlowNode>
  );

  return nodeSize === 'SM' ? SmallQuestionNode({ question, selected, questionType }) : MediumQuestionNode;
};

export default QuestionDetailsNode;
